import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  SupplierData,
  CreateSupplierRequest,
  GetAllSuppliersRequest,
  GetAllSuppliersResponse,
  getAllSuppliers,
  createSupplier as createSupplierAPI,
  SupplierOption,
  getSupplierOptions,
} from '@/application/requests/suppliers';
import { BaseResponse } from '@/application/requests/report';

export interface SupplierListState {
  allSuppliers: SupplierOption[];
  allSuppliersLoading: boolean;
  allSuppliersLoaded: boolean;
  allSuppliersWithInactive: SupplierOption[];
  allSuppliersWithInactiveLoading: boolean;
  allSuppliersWithInactiveLoaded: boolean;
  allSuppliersWithDeleted: SupplierOption[];
  allSuppliersWithDeletedLoading: boolean;
  allSuppliersWithDeletedLoaded: boolean;
  suppliers: SupplierData[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

const initialState: SupplierListState = {
  allSuppliers: [],
  allSuppliersLoading: false,
  allSuppliersLoaded: false,
  allSuppliersWithInactive: [],
  allSuppliersWithInactiveLoading: false,
  allSuppliersWithInactiveLoaded: false,
  allSuppliersWithDeleted: [],
  allSuppliersWithDeletedLoading: false,
  allSuppliersWithDeletedLoaded: false,
  suppliers: [],
  loading: false,
  error: null,
  currentPage: 1,
  totalPages: 1,
  totalItems: 0,
  pageSize: 10,
  hasNext: false,
  hasPrevious: false,
};

export const fetchAllSuppliers = createAsyncThunk(
  'supplier/fetchAllSuppliers',
  async (params: GetAllSuppliersRequest, { rejectWithValue }) => {
    try {
      const response: GetAllSuppliersResponse = await getAllSuppliers(params);
      if (!response.success || !response.data) {
        return rejectWithValue(response.message || 'Failed to fetch suppliers');
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch suppliers');
    }
  }
);

export const createSupplier = createAsyncThunk(
  'supplier/createSupplier',
  async (data: CreateSupplierRequest, { rejectWithValue }) => {
    try {
      const response: BaseResponse<SupplierData> =
        await createSupplierAPI(data);
      if (!response.success || !response.data) {
        return rejectWithValue(response.message || 'Failed to create supplier');
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create supplier');
    }
  }
);

export const fetchAllSuppliersForCombobox = createAsyncThunk(
  'supplier/fetchAllSuppliersForCombobox',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getSupplierOptions();
      if (!response.success || !response.data) {
        return rejectWithValue(
          response.message || 'Failed to fetch all suppliers for combobox'
        );
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch all suppliers for combobox'
      );
    }
  }
);

export const fetchAllSuppliersForComboboxWithInactive = createAsyncThunk(
  'supplier/fetchAllSuppliersForComboboxWithInactive',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getSupplierOptions({ includeInactive: true });
      if (!response.success || !response.data) {
        return rejectWithValue(
          response.message || 'Failed to fetch all suppliers for combobox'
        );
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch all suppliers for combobox'
      );
    }
  }
);

export const fetchAllSuppliersForComboboxWithDeleted = createAsyncThunk(
  'supplier/fetchAllSuppliersForComboboxWithDeleted',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getSupplierOptions({
        includeDeleted: true,
        includeInactive: true,
      });
      if (!response.success || !response.data) {
        return rejectWithValue(
          response.message || 'Failed to fetch all suppliers for combobox'
        );
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch all suppliers for combobox'
      );
    }
  }
);

export const fetchAllSuppliersHandlers = {
  pending: (state: SupplierListState) => {
    state.loading = true;
    state.error = null;
  },
  fulfilled: (
    state: SupplierListState,
    action: PayloadAction<GetAllSuppliersResponse['data']>
  ) => {
    state.loading = false;
    if (action.payload) {
      state.suppliers = action.payload.data;
      state.totalItems = action.payload.total;
      state.totalPages = action.payload.total_pages;
      state.currentPage = action.payload.page;
      state.hasNext = action.payload.has_next;
      state.hasPrevious = action.payload.has_previous;
    }
  },
  rejected: (
    state: SupplierListState,
    action: PayloadAction<string | unknown>
  ) => {
    state.loading = false;
    state.error = action.payload as string;
    state.suppliers = [];
  },
};

export const createSupplierHandlers = {
  pending: (state: SupplierListState) => {
    state.loading = true;
    state.error = null;
  },
  fulfilled: (
    state: SupplierListState,
    action: PayloadAction<SupplierData>
  ) => {
    state.loading = false;
    if (action.payload) {
      state.allSuppliers = [...state.allSuppliers, action.payload];
    }
  },
  rejected: (
    state: SupplierListState,
    action: PayloadAction<string | unknown>
  ) => {
    state.loading = false;
    state.error = action.payload as string;
  },
};

export const fetchAllSuppliersForComboboxHandlers = {
  pending: (state: SupplierListState) => {
    state.allSuppliersLoading = true;
    state.error = null;
  },
  fulfilled: (
    state: SupplierListState,
    action: PayloadAction<SupplierOption[]>
  ) => {
    state.allSuppliersLoading = false;
    state.allSuppliersLoaded = true;
    state.allSuppliers = action.payload;
  },
  rejected: (
    state: SupplierListState,
    action: PayloadAction<string | unknown>
  ) => {
    state.allSuppliersLoading = false;
    state.allSuppliersLoaded = true;
    if (action.payload !== 'Already loading or loaded') {
      state.error = action.payload as string;
    }
  },
};

export const fetchAllSuppliersForComboboxWithInactiveHandlers = {
  pending: (state: SupplierListState) => {
    state.allSuppliersWithInactiveLoading = true;
    state.error = null;
  },
  fulfilled: (
    state: SupplierListState,
    action: PayloadAction<SupplierOption[]>
  ) => {
    state.allSuppliersWithInactiveLoading = false;
    state.allSuppliersWithInactiveLoaded = true;
    state.allSuppliersWithInactive = action.payload;
  },
  rejected: (
    state: SupplierListState,
    action: PayloadAction<string | unknown>
  ) => {
    state.allSuppliersWithInactiveLoading = false;
    state.allSuppliersWithInactiveLoaded = true;
    if (action.payload !== 'Already loading or loaded') {
      state.error = action.payload as string;
    }
  },
};

export const fetchAllSuppliersForComboboxWithDeletedHandlers = {
  pending: (state: SupplierListState) => {
    state.allSuppliersWithDeletedLoading = true;
    state.error = null;
  },
  fulfilled: (
    state: SupplierListState,
    action: PayloadAction<SupplierOption[]>
  ) => {
    state.allSuppliersWithDeletedLoading = false;
    state.allSuppliersWithDeletedLoaded = true;
    state.allSuppliersWithDeleted = action.payload;
  },
  rejected: (
    state: SupplierListState,
    action: PayloadAction<string | unknown>
  ) => {
    state.allSuppliersWithDeletedLoading = false;
    state.allSuppliersWithDeletedLoaded = true;
    if (action.payload !== 'Already loading or loaded') {
      state.error = action.payload as string;
    }
  },
};

const supplierListSlice = createSlice({
  name: 'supplier',
  initialState,
  reducers: {
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    addSupplier(state, action: PayloadAction<SupplierData>) {
      state.allSuppliers = [
        ...state.allSuppliers,
        { id: action.payload.id, name: action.payload.name },
      ];
      state.suppliers = [...state.suppliers, action.payload];
    },
    deleteSupplier(state, action: PayloadAction<string>) {
      state.allSuppliers = state.allSuppliers.filter(
        (supplier) => supplier.id !== action.payload
      );
      state.suppliers = state.suppliers.filter(
        (supplier) => supplier.id !== action.payload
      );
    },
    editSupplier(state, action: PayloadAction<SupplierData>) {
      const updatedSupplier = action.payload;
      state.allSuppliers = state.allSuppliers.map((supplier) => {
        if (supplier.id === updatedSupplier.id) {
          return { id: updatedSupplier.id, name: updatedSupplier.name };
        }
        return supplier;
      });
      state.suppliers = state.suppliers.map((supplier) => {
        if (supplier.id === updatedSupplier.id) {
          return updatedSupplier;
        }
        return supplier;
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllSuppliers.pending, fetchAllSuppliersHandlers.pending)
      .addCase(fetchAllSuppliers.fulfilled, fetchAllSuppliersHandlers.fulfilled)
      .addCase(fetchAllSuppliers.rejected, fetchAllSuppliersHandlers.rejected)
      .addCase(createSupplier.pending, createSupplierHandlers.pending)
      .addCase(createSupplier.fulfilled, createSupplierHandlers.fulfilled)
      .addCase(createSupplier.rejected, createSupplierHandlers.rejected)
      .addCase(
        fetchAllSuppliersForCombobox.pending,
        fetchAllSuppliersForComboboxHandlers.pending
      )
      .addCase(
        fetchAllSuppliersForCombobox.fulfilled,
        fetchAllSuppliersForComboboxHandlers.fulfilled
      )
      .addCase(
        fetchAllSuppliersForCombobox.rejected,
        fetchAllSuppliersForComboboxHandlers.rejected
      )
      .addCase(
        fetchAllSuppliersForComboboxWithInactive.pending,
        fetchAllSuppliersForComboboxWithInactiveHandlers.pending
      )
      .addCase(
        fetchAllSuppliersForComboboxWithInactive.fulfilled,
        fetchAllSuppliersForComboboxWithInactiveHandlers.fulfilled
      )
      .addCase(
        fetchAllSuppliersForComboboxWithInactive.rejected,
        fetchAllSuppliersForComboboxWithInactiveHandlers.rejected
      )
      .addCase(
        fetchAllSuppliersForComboboxWithDeleted.pending,
        fetchAllSuppliersForComboboxWithDeletedHandlers.pending
      )
      .addCase(
        fetchAllSuppliersForComboboxWithDeleted.fulfilled,
        fetchAllSuppliersForComboboxWithDeletedHandlers.fulfilled
      )
      .addCase(
        fetchAllSuppliersForComboboxWithDeleted.rejected,
        fetchAllSuppliersForComboboxWithDeletedHandlers.rejected
      );
  },
});

export const {
  setCurrentPage,
  setPageSize,
  addSupplier,
  deleteSupplier,
  editSupplier,
} = supplierListSlice.actions;
export default supplierListSlice.reducer;
